import { lazy } from 'react';
import { CalendarModuleConfig } from './types';

const CalendarWhatsNew = lazy(() => import('./CalendarWhatsNew'));

/**
 * Calendars Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (calendar/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "admin")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/calendar/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
export const calendarsModuleConfig: CalendarModuleConfig = {
  id: 'calendars',
  name: 'Calendars',
  icon: '📅',
  color: 'purple',
  description: 'Manage business calendars and events with public booking system',
  version: '1.3.0',
  enabled: true,
  category: 'productivity',
  order: 15,

  // ROUTES - permissionLevel is resolved from backend at runtime
  routes: [
    {
      path: '/calendars/birds-eye',
      component: lazy(() => import('./BirdsEyeView')),
      exact: true,
      permissionLevel: 'view',
    },
    {
      path: '/calendars/my',
      component: lazy(() => import('./MyCalendars')),
      exact: true,
      permissionLevel: 'view',
    },
    {
      path: '/calendars/new',
      component: lazy(() => import('./NewCalendar')),
      permissionLevel: 'create',
    },
    {
      path: '/calendars/:id/edit',
      component: lazy(() => import('./EditCalendar')),
      permissionLevel: 'edit',
    },
    {
      path: '/calendars/view',
      component: lazy(() => import('./CalendarView')),
      permissionLevel: 'view',
    },
    {
      path: '/calendars/:id/view',
      component: lazy(() => import('./CalendarView')),
      permissionLevel: 'view',
    },
    {
      path: '/calendars/:id/event-types',
      component: lazy(() => import('./EventTypesManagement')),
      permissionLevel: 'edit',
    },
    {
      path: '/calendars/:id/availability',
      component: lazy(() => import('./AvailabilitySettings')),
      permissionLevel: 'edit',
    },
    {
      path: '/calendars/:id',
      component: lazy(() => import('./CalendarDetails')),
      permissionLevel: 'view',
    },
    {
      path: '/calendar/admin/calendars',
      component: lazy(() => import('./CalendarAccountsAdmin')),
      permissionLevel: 'admin',
    },
    {
      path: '/calendars/life-goals',
      component: lazy(() => import('./life-goals/LifeGoalsTimeline')),
      permissionLevel: 'view',
    },
    {
      path: '/calendars/whats-new',
      component: CalendarWhatsNew,
      permissionLevel: 'admin',
    }
  ],

  // NAVIGATION - permissionLevel is resolved from backend at runtime
  navigation: [
    {
      label: "Bird's Eye View",
      path: '/calendars/birds-eye',
      icon: '🦅',
      permissionLevel: 'view',
    },
    {
      label: 'My Calendars',
      path: '/calendars/my',
      icon: '📋',
      permissionLevel: 'view',
    },
    {
      label: 'Life Goals Timeline',
      path: '/calendars/life-goals',
      icon: '🎯',
      permissionLevel: 'view',
    },
    {
      label: 'New Calendar',
      path: '/calendars/new',
      icon: '➕',
      permissionLevel: 'create',
    },
    {
      label: 'Calendar Admin',
      path: '/calendar/admin/calendars',
      icon: '⚙️',
      permissionLevel: 'admin',
    },
    {
      label: "What's New",
      path: '/calendars/whats-new',
      icon: '🎉',
      permissionLevel: 'admin',
    }
  ],

  // Permissions come from the backend (single source of truth).
  // See: business-builder-backend/src/modules/calendar/permissions.ts
  // These empty arrays get replaced at runtime by ModulePermissionsContext.
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
    admin: [],
    share: [],
  },
  quickActions: [
    {
      label: 'My Calendars',
      path: '/calendars/my',
      icon: '📋',
      description: 'View all your calendars'
    },
    {
      label: 'New Calendar',
      path: '/calendars/new',
      icon: '➕',
      description: 'Create a new calendar'
    }
  ],

  // 📋 CHANGELOG
  changelog: [
    {
      version: "1.3.0",
      releaseDate: "2025-11-24",
      title: "HTML Email Support with WYSIWYG Editor & Tenant Branding",
      features: [
        "✅ Integrated WYSIWYG HTML email editor for calendar email broadcasts",
        "✅ Rich text formatting: bold, italic, underline, lists, links, code blocks, text alignment",
        "✅ Hyperlinks in emails are now clickable (main issue resolved!)",
        "✅ Visual and source editing modes for HTML emails",
        "✅ HTML preview and plain text preview tabs",
        "✅ Tenant branding applied to all broadcast emails (logo, colors, site name)",
        "✅ Professional email templates with header and footer branding",
        "✅ Links styled with tenant primary color for brand consistency",
        "✅ Mobile-responsive email templates",
        "✅ Plain text fallback for email clients that don't support HTML"
      ],
      notes: "Major upgrade to email broadcast system. Users can now create rich, formatted emails with clickable links using a professional WYSIWYG editor. All broadcast emails automatically include tenant branding (logo and colors) for a polished, professional appearance. This resolves the critical issue where plain text links were not clickable in emails."
    },
    {
      version: "1.2.0",
      releaseDate: "2025-11-22",
      title: "SMS Sender Configuration & Event Deletion Improvements",
      features: [
        "✅ Enhanced SMS sender selection with visual radio button interface",
        "✅ Display both Alpha ID and Dedicated Number options with tenant-specific values",
        "✅ Fixed GraphQL query to properly fetch tenant SMS configuration (alphaId, dedicatedNumber)",
        "✅ Auto-set broadcast event end time to start time + 1 minute (SMS, Email, Both)",
        "✅ Smart time formatting for datetime-local inputs (YYYY-MM-DDTHH:MM)",
        "✅ Only auto-set end time when creating events, not when editing",
        "✅ Made Event ID copyable with click-to-copy button and selectable text",
        "✅ Fixed event deletion to permanently remove from database (not just mark as cancelled)",
        "✅ Added DELETE_EVENT mutation for proper database cleanup",
        "✅ Updated confirmation message to clarify deletion prevents broadcasts",
        "✅ Changed button text from 'Cancel Event' to 'Delete Event' for clarity",
        "✅ Removed debug console logging for cleaner production environment"
      ],
      notes: "Critical fixes for SMS broadcast configuration and event deletion. The new sender selection UI clearly shows both Alpha ID and Dedicated Number options with actual tenant values. Event deletion now properly removes records from the database, preventing scheduled broadcasts from being sent for deleted events. The auto-end-time feature for broadcast events improves UX by automatically setting appropriate durations for instant broadcast triggers."
    },
    {
      version: "1.1.0",
      releaseDate: "2024-09-08",
      title: "Enhanced Broadcast System with Intuitive Tag-Based Selection",
      features: [
        "✅ Tag-based client group selection for SMS/Email broadcasts",
        "✅ Always-visible tag browser (no button clicking required)",
        "✅ Click tags to instantly select all clients in that group",
        "✅ Remove individual recipients with X button after group selection",
        "✅ 'Clear All' button to deselect all recipients at once",
        "✅ Real-time recipient preview showing exactly who will receive the broadcast",
        "✅ Hover tooltips on tags preview client names before selection",
        "✅ Double-click tags to expand and view full client details",
        "✅ Improved event form flow: Date/Time moved above Event Type for better UX",
        "✅ Step-by-step broadcast configuration (Step 1: SMS, Step 2: Email, Step 3: Recipients)",
        "✅ Fixed critical bug: Emails now send correctly in BOTH_BROADCAST mode",
        "✅ Separate tracking for SMS and Email broadcasts to prevent cross-channel conflicts",
        "✅ Client tags displayed in recipient preview with phone numbers",
        "✅ Comprehensive broadcast testing checklist added"
      ],
      notes: "Major UX overhaul of the broadcast system with intuitive tag-based selection and step-by-step configuration flow. Fixed a critical bug where emails were incorrectly skipped when sending both SMS and email broadcasts. The new linear workflow guides users through message creation and recipient selection with clear visual steps."
    },
    {
      version: "1.0.0",
      releaseDate: "2023-04-15",
      title: "Production Release - Complete Calendar & Booking System",
      features: [
        "✅ Multi-calendar support with color-coded views (Month/Week/Day/Agenda)",
        "✅ Mobile-responsive design with touch-optimized controls",
        "✅ Click-to-create events in all calendar views (including mobile tap support)",
        "✅ Drag & drop event scheduling with time adjustment",
        "✅ Public booking system (Calendly-style) with shareable links",
        "✅ Event type management with custom durations and pricing",
        "✅ Custom booking questions (short text, long text, dropdown, radio, checkboxes)",
        "✅ Availability scheduling with timezone support (14 timezones)",
        "✅ Blocked dates and time-off management",
        "✅ iCal integration (import/export, email invites)",
        "✅ Email-to-calendar event creation",
        "✅ SMS broadcast scheduling",
        "✅ Event notes and task checklists",
        "✅ Monthly goals with checkpoint tracking",
        "✅ Life goals timeline with long-term planning",
        "✅ Calendar sharing with granular permissions",
        "✅ Multi-timezone display for international teams",
        "✅ Recurring events (RRULE format)",
        "✅ Event filtering by type, tags, and calendar",
        "✅ Scroll position persistence with localStorage",
        "✅ Calendar admin dashboard for system management"
      ],
      notes: "First production release includes complete calendar management system with professional booking capabilities, mobile optimization, and extensive integration options."
    }
  ],

  // 🗺️ ROADMAP
  roadmap: [
    {
      title: "Enhanced Mobile Experience",
      description: "Continue improving mobile responsiveness and touch interactions",
      targetVersion: "1.1.0",
      priority: "high",
      status: "in-progress",
      features: [
        "✅ Mobile-friendly event modal (full-screen on mobile)",
        "✅ Touch-optimized calendar controls",
        "✅ Responsive calendar toolbar with wrapping badges",
        "⏳ Swipe gestures for week/day navigation",
        "⏳ Mobile-optimized event type cards",
        "⏳ Touch-friendly time picker for bookings"
      ]
    },
    {
      title: "Advanced Booking Features",
      description: "Enhanced public booking system with payment integration and automation",
      targetVersion: "1.2.0",
      priority: "high",
      status: "planned",
      features: [
        "Payment processing for paid bookings (Stripe integration)",
        "Booking confirmation emails with calendar attachments",
        "SMS reminders for upcoming bookings",
        "Automated follow-up sequences",
        "Buffer time between bookings",
        "Team scheduling (round-robin, collective availability)",
        "Booking analytics and conversion tracking",
        "Custom branding for booking pages"
      ]
    },
    {
      title: "Calendar Integrations",
      description: "Two-way sync with external calendar systems",
      targetVersion: "1.3.0",
      priority: "medium",
      status: "planned",
      features: [
        "Google Calendar bi-directional sync",
        "Microsoft Outlook/Office 365 sync",
        "Apple iCloud calendar sync",
        "Zoom meeting auto-creation",
        "Microsoft Teams meeting links",
        "Google Meet integration",
        "Webhook support for external systems"
      ]
    },
    {
      title: "Team Collaboration",
      description: "Enhanced features for team calendar management",
      targetVersion: "1.4.0",
      priority: "medium",
      status: "planned",
      features: [
        "Resource booking (meeting rooms, equipment)",
        "Team availability view (see everyone's calendar)",
        "Calendar overlays for comparison",
        "Shared event templates",
        "Team-wide blocked dates",
        "Delegation and proxy booking",
        "Department-specific calendars"
      ]
    },
    {
      title: "Analytics & Reporting",
      description: "Insights into calendar usage and booking performance",
      targetVersion: "1.5.0",
      priority: "low",
      status: "planned",
      features: [
        "Booking conversion rates",
        "Popular time slots analysis",
        "Event type performance metrics",
        "No-show tracking and reporting",
        "Revenue analytics for paid bookings",
        "Calendar utilization reports",
        "Custom booking reports and exports"
      ]
    },
    {
      title: "Automation & AI",
      description: "Intelligent scheduling and automated workflows",
      targetVersion: "2.0.0",
      priority: "low",
      status: "planned",
      features: [
        "AI-powered optimal time suggestions",
        "Smart meeting duration predictions",
        "Automated rescheduling for conflicts",
        "Intelligent booking question suggestions",
        "Natural language event creation",
        "Predictive availability forecasting",
        "Auto-categorization of event types"
      ]
    }
  ]
};